import { ApiMetadata, ModelRow, ObjectUtil, UserSettings } from "@mcleod/core";

/**
 * Shared utility class for SQL generation in menu actions
 */
export class MenuActionSQLUtils {

    /**
     * Creates an INSERT statement for a given table and data
     */
    static createInsertStatement(tableName: string, insertData: Record<string, any>): string {
        const keys = Object.keys(insertData);
        const values = keys.map(key => {
            const value = insertData[key];
            if (value === null || value === undefined) return 'null';
            if (typeof value === 'string') return `'${value.replace(/'/g, "''")}'`;
            return String(value);
        });

        return `insert into ${tableName} (${keys.join(", ")}) values (${values.join(", ")});`;
    }

    /**
     * Creates an UPDATE statement for a given table and data
     */
    static createUpdateStatement(tableName: string, updateData: Record<string, any>, model: ModelRow): string {
        const dataUpdateParts = [];
        for (const [key, value] of Object.entries(updateData)) {
            if (value === null || value === undefined) {
                dataUpdateParts.push(`${key} = null`);
            } else if (typeof value === 'string') {
                dataUpdateParts.push(`${key} = '${value.replace(/'/g, "''")}'`);
            } else {
                dataUpdateParts.push(`${key} = ${value}`);
            }
        }
        const dataUpdateString = dataUpdateParts.join(", ");
        return `update ${tableName} set ${dataUpdateString} ${this.getWhereClause(model)};`;
    }

    /**
     * Creates a DELETE statement for a given table and model
     */
    static createDeleteStatement(tableName: string, model: ModelRow): string {
        return `delete from ${tableName} ${this.getWhereClause(model)};`;
    }

    /**
     * Generates WHERE clause for a model
     */
    static getWhereClause(model: ModelRow): string {
        return `where id = '${model.get("id")}' and company_id = '${UserSettings.get()?.company_id}'`;
    }

    /**
     * Prepares insert data for a ModelRow
     */
    static getInsertData(modelRow: ModelRow): Record<string, any> {
        const meta: ApiMetadata = modelRow.getMetadata();
        const data = ObjectUtil.deepCopy({...modelRow.data});
        const insertData: Record<string, any> = {
            id: data.id,
            company_id: UserSettings.get()?.company_id
        };
        delete data.id;
        delete data.company_id;

        for (const [key, value] of Object.entries(data)) {
            const field = meta.output[key];
            if (field?.allowUpdate !== false) {
                insertData[key] = value;
            }
        }

        return insertData;
    }

    /**
     * Prepares update data for a ModelRow
     */
    static getUpdateData(modelRow: ModelRow): Record<string, any> {
        const meta: ApiMetadata = modelRow.getMetadata();
        const data = ObjectUtil.deepCopy({...modelRow.data});
        const updateData: Record<string, any> = { };
        delete data.id;
        delete data.company_id;

        for (const [key, value] of Object.entries(data)) {
            const field = meta.output[key];
            if (field?.allowUpdate !== false && value !== modelRow.originalData?.[key]) {
                updateData[key] = value;
            }
        }

        return updateData;
    }
}
